import * as moment from 'moment'
import axios from '../src/axios'
import { ENDPOINTS } from '../src/constants'

declare const window: any

const STORAGE_KEY = 'metaClickRef'
// Mirrors Meta's own default click-attribution window, so a ref we've been holding longer
// than this is treated as unattributed rather than appended to a tracking link.
const MAX_REF_AGE_DAYS = 7

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Captures `fbclid`/`_fbp` and records them against a new `meta_ad_attributions` row, but
 * only when `fbclid` is actually present in the URL — i.e. only on a genuine Meta-ad click,
 * never on an ordinary page load. Persists the returned row id in localStorage the same way
 * `referralId` is persisted (utils/../src/axios.ts's `attachReferralHeaders`), so it survives
 * navigation through to whichever product the visitor eventually clicks.
 */
export const captureMetaAdClick = async (location: {
  search: string
  href: string
}): Promise<void> => {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams(location.search)
  const fbclid = params.get('fbclid')
  if (!fbclid) return

  // `TrackingComponent`'s effect has no dependency array, so it can rerun on any re-render
  // of this globally-mounted component (e.g. app-state dispatches during bootstrap), not
  // only on navigation. Skip re-capturing when the URL still carries the same fbclid we
  // already recorded, so one ad click can't mint multiple `meta_ad_attributions` rows.
  const alreadyStored = JSON.parse(
    window.localStorage.getItem(STORAGE_KEY) || '{}'
  )
  if (alreadyStored?.fbclid === fbclid) return

  try {
    const response = await axios.post(ENDPOINTS.metaClick, {
      fbclid,
      fbp: getCookie('_fbp'),
      landingUrl: location.href,
    })

    const id = response?.data?.data?.id
    if (!id) return

    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ id, fbclid, time: moment().format() })
    )
  } catch (error) {
    // Best-effort — a failed capture just means this session's later purchases won't be
    // sent to Meta CAPI, not a broken checkout/tracking flow.
  }
}

/**
 * The `meta_ad_attributions` row id to embed as `-f{id}` in a tracking link's SubID
 * (see utils/brandsHelper.ts#generateTrackingLink), or null if no ref was captured or it's
 * aged out of the attribution window.
 */
export const getMetaClickRefId = (): number | null => {
  if (typeof window === 'undefined') return null

  const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')
  if (!stored?.id) return null

  const ageInDays = moment().diff(moment(stored.time), 'days')
  if (ageInDays > MAX_REF_AGE_DAYS) return null

  return Number(stored.id)
}
